import React, { useCallback, useEffect, useState } from "react";
import { ResponsivePie } from "@nivo/pie";
import { Grid2 } from "@mui/material";

import {
  AssetOrLapsesChartData,
  CountChartData,
  TrackTruckData,
} from "../../../../Models/Models";
import { GetAssetCount } from "../../../../ApiServices/ApiRequests";
import { useTranslation } from "react-i18next";
import {
  CtmLiveOverViewType,
  useCtmLiveOverView,
} from "../../../../ContextAPI/CtmLiveOverViewContext";
import ChartWebSocket from "../../../../WebSockets/ChartWebSocket";
import Loading from "../../../../Controller/LoadingController/Loading";
// import DataServices from '../../Services/DataServices';
// import ChartWebSocket from '../../WebSockets/ChartWebSocket';

export const AssetsChart = () => {
  const { ctmLiveOverViewState, dispatchCtmLiveOverView } =
    useCtmLiveOverView();
  const { t } = useTranslation();

  const handleInputChange = useCallback(
    (
      field: keyof CtmLiveOverViewType,
      value: string | boolean | number | null | AssetOrLapsesChartData[]
    ) => {
      dispatchCtmLiveOverView({ type: "SET_FIELD", field, value });
    },
    [dispatchCtmLiveOverView]
  );

  const handleBulkChange = useCallback(
    (value: Partial<CtmLiveOverViewType>) => {
      dispatchCtmLiveOverView({ type: "BULK_UPDATE", value });
    },
    [dispatchCtmLiveOverView]
  );

  const [loading, setLoading] = useState<boolean>(false);

  // Async function to retrieve data
  const retrieveData = async (): Promise<void> => {
    try {
      setLoading(true);
      const result: CountChartData[] = await GetAssetCount();
      const formattedData: AssetOrLapsesChartData[] = result?.map((ac) => ({
        id: ac.name,
        label: ac.name,
        value: ac.value,
      }));

      const uniqueArray: AssetOrLapsesChartData[] = Array.from(
        new Map(formattedData?.map((item) => [item.id, item])).values()
      );
      handleInputChange("AssetCountChartData", uniqueArray);
    } catch (e) {
      console.error("Error fetching  Asset Count data:", e);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    retrieveData();
  }, []);

  // Sort and slice the top 5 data points
  const chartInfo: AssetOrLapsesChartData[] =
    ctmLiveOverViewState.AssetCountChartData?.sort(
      (a, b) => b.value - a.value
    ).slice(0, 5);
  if (chartInfo) {
    return (
      <Grid2 style={{ height: 95 }}>
        {loading ? (
          <Loading loading={loading} type={`dots`} backDrop={false} />
        ) : (
          <ResponsivePie
            data={chartInfo}
            margin={{ right: 0, bottom: 5, left: 140, top: 3 }}
            innerRadius={0.5}
            padAngle={0.7}
            cornerRadius={3}
            colors={{ scheme: "category10" }}
            borderWidth={1}
            borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
            enableArcLabels={false}
            enableArcLinkLabels={false}
            theme={{
              text: {
                color: "black",
              },
              tooltip: {
                container: {
                  color: "black",
                  background: "white",
                },
              },
            }}
            defs={[
              {
                id: "dots",
                type: "patternDots",
                background: "inherit",
                color: "rgba(255, 255, 255, 0.3)",
                size: 4,
                padding: 1,
                stagger: true,
              },
              {
                id: "lines",
                type: "patternLines",
                background: "inherit",
                color: "rgba(255, 255, 255, 0.3)",
                rotation: -45,
                lineWidth: 6,
                spacing: 10,
              },
            ]}
            fill={[
              {
                match: {
                  id: "ruby",
                },
                id: "dots",
              },
              {
                match: {
                  id: "c",
                },
                id: "dots",
              },
              {
                match: {
                  id: "go",
                },
                id: "dots",
              },
              {
                match: {
                  id: "python",
                },
                id: "dots",
              },
              {
                match: {
                  id: "scala",
                },
                id: "lines",
              },
              {
                match: {
                  id: "lisp",
                },
                id: "lines",
              },
              {
                match: {
                  id: "elixir",
                },
                id: "lines",
              },
              {
                match: {
                  id: "javascript",
                },
                id: "lines",
              },
            ]}
            legends={[
              {
                anchor: "bottom-left",
                direction: "column",
                justify: false,
                translateX: -120,
                translateY: -10,
                itemWidth: 10,
                itemHeight: 5,
                itemsSpacing: 10,
                symbolSize: 12,
                itemDirection: "left-to-right",

                effects: [
                  {
                    on: "hover",
                    style: {
                      itemTextColor: "#000",
                    },
                  },
                ],
                //data: chartData.sort((a, b) => b.value - a.value).slice(0, 5),
              },
            ]}
          />
        )}
        <ChartWebSocket />
      </Grid2>
    );
  } else {
    return null;
  }
};
